import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import { FHTextarea } from '../../../base';
import { clientFamilyBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { TreatmentPlanResourceVersion } from '../constants/general';
import { getAllInvalidFieldNames, validateSync } from '../../../../helpers';
import { FamilyBackgroundSchema } from './validationSchema';

import type { RootState } from '../../../../store';

function FamilyBackground() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { clientFamilyBackground } = useSelector(
    (state: RootState) => state.clientFamilyBackgrounds,
  );

  const [formData, setFormData] = useState({
    family_medical_history_maternal: '',
    family_medical_history_paternal: '',
    community_involvement_religious_affiliations: '',
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (clientFamilyBackground) {
      setFormData({
        family_medical_history_maternal:
          clientFamilyBackground.family_medical_history_maternal || '',
        family_medical_history_paternal:
          clientFamilyBackground.family_medical_history_paternal || '',
        community_involvement_religious_affiliations:
          clientFamilyBackground.community_involvement_religious_affiliations || '',
      });
    }
  }, [clientFamilyBackground]);

  useEffect(() => {
    if (!treatmentPlan || !clientFamilyBackground) return;

    const errorsData = getAllInvalidFieldNames(FamilyBackgroundSchema, {
      ...formData,
      client_family_background_version:
        treatmentPlan?.resource_versions?.client_and_family_background ||
        TreatmentPlanResourceVersion.V1,
    });
    setErrors(errorsData);
  }, [formData, treatmentPlan, clientFamilyBackground]);

  useEffect(() => {
    const errorCount = Object.values(errors).filter(Boolean).length;
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'familyBackgroundErrorCount',
        data: errorCount,
      }),
    );
  }, [errors]);

  const handleFieldChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'clientFamilyBackground',
        data: { ...clientFamilyBackground, [key]: value },
      }),
    );
  };

  const handleBlur = async () => {
    if (!treatmentPlan || !clientFamilyBackground) return;

    if (
      !validateSync(FamilyBackgroundSchema, {
        ...formData,
        client_family_background_version:
          treatmentPlan?.resource_versions?.client_and_family_background ||
          TreatmentPlanResourceVersion.V1,
      })
    )
      return;

    const payload = {
      ...clientFamilyBackground,
      ...formData,
    };

    if (!clientFamilyBackground?.id) {
      const response = await dispatch(
        clientFamilyBackgroundActions.createClientFamilyBackground({
          treatmentPlanId: treatmentPlan.id,
          data: payload,
          getAccessTokenSilently,
        }),
      );
      if (response.error) return;
    } else {
      dispatch(
        clientFamilyBackgroundActions.updateClientFamilyBackground({
          treatmentPlanId: treatmentPlan.id,
          data: payload,
          getAccessTokenSilently,
        }),
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography fontSize={18} fontWeight="bold" textAlign="left">
        Family background
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Family Medical History Maternal side*
          </Typography>
          <FHTextarea
            data-testid="familyMedicalHistoryMaternalTextField"
            value={formData.family_medical_history_maternal}
            maxRows={4}
            fullWidth
            disabled={!isEditable}
            onEdit={(e) => handleFieldChange('family_medical_history_maternal', e.target.value)}
            onBlur={handleBlur}
            error={errors['family_medical_history_maternal']}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Family Medical history Paternal side*
          </Typography>
          <FHTextarea
            data-testid="familyMedicalHistoryPaternalTextField"
            value={formData.family_medical_history_paternal}
            maxRows={4}
            fullWidth
            disabled={!isEditable}
            onEdit={(e) => handleFieldChange('family_medical_history_paternal', e.target.value)}
            onBlur={handleBlur}
            error={errors['family_medical_history_paternal']}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography fontWeight="bold" textAlign="left" mb={1}>
            Community involvement or religious affiliation(s)*
          </Typography>
          <FHTextarea
            data-testid="communityInvolvementReligiousAffiliationsTextField"
            value={formData.community_involvement_religious_affiliations}
            maxRows={4}
            fullWidth
            disabled={!isEditable}
            onEdit={(e) =>
              handleFieldChange('community_involvement_religious_affiliations', e.target.value)
            }
            onBlur={handleBlur}
            error={errors['community_involvement_religious_affiliations']}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default FamilyBackground;
